import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "./utils/supabaseClient";
import { getTenantBranding, isBankTransferConfigured } from "./utils/tenantBranding";
import {
    Box,
    Button,
    Center,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    Icon,
    useToast,
    Spinner,
    Alert,
    AlertIcon,
    Badge,
    Divider,
    SimpleGrid,
    Stack,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon
} from "@chakra-ui/react";
import { CheckCircleIcon, ArrowBackIcon, EditIcon } from "@chakra-ui/icons";
import { HiUser, HiMail, HiPhone, HiIdentification, HiCalendar, HiGlobeAlt, HiCreditCard, HiInformationCircle, HiQrcode, HiOfficeBuilding, HiDocumentText } from "react-icons/hi";
import { motion } from "framer-motion";
import moment from "moment";
import { useRouteConfig } from "./providers/RouteConfigProvider";
import { PayNowQRCode, useOrganizationPayNowDetails } from "./components/PayNowQRCode";
import { buildPayNowReference } from "./utils/paynowReference";
import { getTakingCourseInSingapore, shouldApplyGst } from "./utils/locationBasedGst";
import { fetchIsStripePaymentConfigured } from "./utils/stripePaymentAvailability";

const MotionBox = motion(Box);

const InfoRow = ({ icon, label, value, ...props }) => (
    <HStack spacing={3} py={2} {...props}>
        {icon && <Icon as={icon} color="brand.500" boxSize={4} />}
        <Box flex="1">
            <Text fontSize="xs" color="gray.500" fontWeight="500">{label}</Text>
            <Text fontSize="sm" fontWeight="600" color="gray.800">{value || 'Not provided'}</Text>
        </Box>
    </HStack>
);

const SectionBox = ({ title, icon, children, ...props }) => (
    <Box
        borderWidth="1px"
        borderRadius="lg"
        p={4}
        bg="white"
        borderColor="gray.200"
        {...props}
    >
        <HStack mb={3} pb={2} borderBottom="1px solid" borderColor="gray.100">
            {icon && <Icon as={icon} color="brand.600" boxSize={5} />}
            <Heading size="xs" color="brand.700">{title}</Heading>
        </HStack>
        {children}
    </Box>
);

export const RegistrationPending = () => {
    const { registrationID } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const { fullCertRegistrationPath } = useRouteConfig();
    const [registrationData, setRegistrationData] = useState(null);
    const [certificationRun, setCertificationRun] = useState(null);
    const [invoice, setInvoice] = useState(null);
    const [lineItems, setLineItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [showPayNowModal, setShowPayNowModal] = useState(false);
    const webhookSentRef = useRef(false); // Prevent duplicate webhook calls
    
    // Organization GST settings
    const [orgGstSettings, setOrgGstSettings] = useState({
        gst_enabled: false,
        gst_rate: 0
    });
    const payNowOrg = useOrganizationPayNowDetails();
    const payNowReference = buildPayNowReference({
        programmeCode: certificationRun?.full_certification?.code,
        intake: certificationRun?.intake,
        participantName: registrationData?.participant_name,
    });

    const getEffectiveGstEnabled = () =>
        shouldApplyGst({
            orgGstEnabled: !!orgGstSettings.gst_enabled,
            locationBasedGst: certificationRun?.full_certification?.location_based_gst === true,
            nationality: registrationData?.nationality,
            takingCourseInSingapore: getTakingCourseInSingapore(registrationData?.additional_data),
        });
    
    // Organization default contact email
    const [orgContactEmail, setOrgContactEmail] = useState('');
    const [stripeEnabled, setStripeEnabled] = useState(false);

    useEffect(() => {
        let cancelled = false;
        fetchIsStripePaymentConfigured(supabase).then((enabled) => {
            if (!cancelled) setStripeEnabled(enabled);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    const handlePayNowDone = async () => {
        setIsUpdatingStatus(true);
        try {
            const { error: updateError } = await supabase
                .from('registration')
                .update({ registration_stage: 'payment_in_progress' })
                .eq('id', registrationID);

            if (updateError) throw updateError;

            toast({
                title: "Payment Recorded",
                    description: "Your registration status has been updated to 'payment in progress'. We will verify the payment shortly.",
                status: "success",
                duration: 5000,
                isClosable: true,
            });
            
            // Refresh data
            window.location.reload();
        } catch (err) {
            console.error('Error updating status:', err);
            toast({
                title: "Error",
                description: "Could not update status. Please contact support.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsUpdatingStatus(false);
            setShowPayNowModal(false);
        }
    };

    useEffect(() => {
        const fetchRegistration = async () => {
            setLoading(true);
            try {
                // Get registration details
                const { data: regData, error: regError } = await supabase
                    .from("registration")
                    .select("*, invoices(*, invoice_line_items(*))")
                    .eq("id", registrationID)
                    .single();

                if (regError) throw regError;
                console.log(regData, 'regData');

                setRegistrationData(regData);

                // If there's a draft_cert_run_code, fetch certification run details
                let fetchedCertRun = null;
                if (regData.draft_cert_run_code) {
                    const { data: certRun, error: certError } = await supabase
                        .from("full_certification_runs")
                        .select("*, full_certification(*)")
                        .eq("id", regData.draft_cert_run_code)
                        .single();

                    if (!certError && certRun) {
                        setCertificationRun(certRun);
                        fetchedCertRun = certRun;
                    }
                }
                
                // Fetch organization GST settings
                try {
                    const { data: orgData, error: orgError } = await supabase
                        .from('organization')
                        .select('gst_enabled, gst_rate')
                        .limit(1)
                        .maybeSingle();
                    
                    if (!orgError && orgData) {
                        setOrgGstSettings({
                            gst_enabled: orgData.gst_enabled || false,
                            gst_rate: parseFloat(orgData.gst_rate) || 0
                        });
                        console.log('Organization GST settings loaded:', orgData);
                    }
                } catch (err) {
                    console.error('Error fetching organization GST settings:', err);
                }
                
                // Display-from address from safe system_settings (no org email account metadata)
                try {
                    const { data: fromSetting } = await supabase
                        .from('system_settings')
                        .select('setting_value')
                        .eq('setting_key', 'email_from_address')
                        .maybeSingle();

                    if (fromSetting?.setting_value) {
                        setOrgContactEmail(fromSetting.setting_value);
                        console.log('Organization contact email loaded:', fromSetting.setting_value);
                    }
                } catch (err) {
                    console.error('Error fetching organization contact email:', err);
                }

                // Send registration email via Outlook
                // Only send if email hasn't been sent yet (check enrollment_email_isSent) and we haven't sent it in this session
                if (!regData.enrollment_email_isSent && !webhookSentRef.current) {
                    webhookSentRef.current = true; // Mark as sent to prevent duplicate calls
                    const isFullCertification = !!regData.draft_cert_run_code;
                    
                    console.log('📧 Starting Outlook email send from RegistrationPending for:', registrationID);
                    console.log('📧 Is full certification:', isFullCertification);
                    
                    // Send email asynchronously (don't block UI)
                    (async () => {
                        try {
                            const { sendRegistrationEmail } = await import('./utils/outlookEmailHelper');

                            // Look up this school's template id. The deployed copy-google-doc
                            // function requires templateId. Section stays put if the name changes.
                            console.log('📄 Generating registration form for attachment...');
                            let attachments = [];
                            try {
                                const { data: formTemplates, error: formTemplateError } = await supabase
                                    .from('google_document_templates')
                                    .select('id, name')
                                    .eq('is_active', true)
                                    .eq('section', 'Initial Registration')
                                    .order('display_order', { ascending: true })
                                    .limit(1);

                                const templateId = formTemplates?.[0]?.id;
                                if (formTemplateError || !templateId) {
                                    console.error('❌ Registration form template id not found:', formTemplateError || 'no active template in Initial Registration');
                                } else {
                                    // copy-google-doc resolves the default doc-gen Google account server-side
                                    const { data: genResult, error: genError } = await supabase.functions.invoke('copy-google-doc', {
                                        body: {
                                            registrationIds: [registrationID],
                                            action: 'generate_single',
                                            templateId,
                                            returnBase64: true
                                        }
                                    });

                                    if (!genError && genResult?.success && genResult?.pdfBase64) {
                                        console.log('✅ Registration form generated successfully!');
                                        attachments.push({
                                            name: `${genResult.documentName || formTemplates[0].name || 'Registration Form'}.pdf`,
                                            contentType: 'application/pdf',
                                            contentBytes: genResult.pdfBase64
                                        });
                                    } else {
                                        console.error('❌ Failed to generate registration form for attachment:', genError || genResult?.error);
                                    }
                                }
                            } catch (genErr) {
                                console.error('❌ Error invoking copy-google-doc for attachment:', genErr);
                            }

                            if (isFullCertification && fetchedCertRun) {
                                // Full certification - fetch modules
                                console.log('📧 Fetching modules for full certification...');
                                
                                // Get all registrations for this certification run
                                const { data: allRegistrations, error: regsError } = await supabase
                                    .from('registration')
                                    .select('id, draft_course_run_code')
                                    .eq('draft_cert_run_code', regData.draft_cert_run_code)
                                    .eq('participant_email', regData.participant_email);
                                
                                if (regsError) throw regsError;
                                
                                // Get registration modules
                                const registrationIds = allRegistrations?.map(r => r.id) || [registrationID];
                                const { data: modules, error: modulesError } = await supabase
                                    .from('registration_modules')
                                    .select('course_run_code')
                                    .in('registration_id', registrationIds);
                                
                                if (modulesError) throw modulesError;
                                
                                // Get course run details for each module
                                const courseRunCodes = modules?.map(m => m.course_run_code).filter(Boolean) || [];
                                let moduleDetails = [];
                                
                                if (courseRunCodes.length > 0) {
                                    const { data: courseRuns, error: courseRunsError } = await supabase
                                        .from('course_run')
                                        .select('id, start_date, end_date, course_type:course_type_code(name)')
                                        .in('id', courseRunCodes);
                                    
                                    if (!courseRunsError && courseRuns) {
                                        moduleDetails = courseRuns.map(cr => ({
                                            name: cr.course_type?.name || 'Module',
                                            start_date: cr.start_date,
                                            end_date: cr.end_date,
                                        }));
                                    }
                                }
                                
                                console.log('📧 Found', moduleDetails.length, 'modules');
                                
                                // Send full certification email
                                const emailResult = await sendRegistrationEmail({
                                    participantEmail: regData.participant_email,
                                    participantName: regData.participant_name,
                                    courseName: fetchedCertRun.full_certification?.name || fetchedCertRun.run_name || 'Full Certification',
                                    startDate: fetchedCertRun.start_date,
                                    endDate: fetchedCertRun.end_date,
                                    registrationType: regData.registration_type,
                                    isFullCertification: true,
                                    modules: moduleDetails,
                                    registrationId: registrationID, // Update first registration, others will be updated separately if needed
                                    attachments: attachments,
                                });
                                
                                if (emailResult.success) {
                                    console.log('✅ Full certification email sent successfully via Outlook!', emailResult);
                                } else {
                                    console.error('❌ Full certification email failed:', emailResult);
                                }
                            } else {
                                // Individual course - fetch course run and sessions
                                console.log('📧 Fetching course details for individual course...');
                                
                                let courseName = 'Course';
                                let startDate = null;
                                let endDate = null;
                                let sessions = [];
                                
                                if (regData.draft_course_run_code) {
                                    const { data: courseRun, error: courseRunError } = await supabase
                                        .from('course_run')
                                        .select('id, start_date, end_date, course_type:course_type_code(name)')
                                        .eq('id', regData.draft_course_run_code)
                                        .single();
                                    
                                    if (!courseRunError && courseRun) {
                                        courseName = courseRun.course_type?.name || 'Course';
                                        startDate = courseRun.start_date;
                                        endDate = courseRun.end_date;
                                        
                                        // Fetch sessions
                                        const { data: sessionData, error: sessionError } = await supabase
                                            .from('session')
                                            .select('*')
                                            .eq('course_run_code', courseRun.id)
                                            .order('session_date', { ascending: true });
                                        
                                        if (!sessionError && sessionData) {
                                            sessions = sessionData.map(session => ({
                                                date: session.session_date,
                                                start_time: session.start_time,
                                                end_time: session.end_time,
                                                venue: session.custom_venue || session.venue_id,
                                                room: session.custom_room || session.room_id,
                                            }));
                                        }
                                    }
                                }
                                
                                console.log('📧 Course:', courseName, 'Sessions:', sessions.length);

                                // Send individual course email
                                const emailResult = await sendRegistrationEmail({
                                    participantEmail: regData.participant_email,
                                    participantName: regData.participant_name,
                                    courseName: courseName,
                                    startDate: startDate,
                                    endDate: endDate,
                                    registrationType: regData.registration_type,
                                    isFullCertification: false,
                                    sessions: sessions,
                                    registrationId: registrationID,
                                    attachments: attachments,
                                });
                                
                                if (emailResult.success) {
                                    console.log('✅ Individual course email sent successfully via Outlook!', emailResult);
                        } else {
                                    console.error('❌ Individual course email failed:', emailResult);
                        }
                            }
                        } catch (emailError) {
                            console.error('❌ Error sending registration email via Outlook from RegistrationPending:', emailError);
                            console.error('Error details:', {
                                message: emailError.message,
                                stack: emailError.stack,
                                error: emailError
                            });
                        }
                    })();
                } else {
                    console.log('Email already sent (enrollment_email_isSent is true), skipping email send');
                }

            } catch (error) {
                console.error("Error fetching registration:", error);
                toast({
                    title: "Error",
                    description: "Could not load registration details.",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            } finally {
                setLoading(false);
            }
        };

        fetchRegistration();
    }, [registrationID, toast]);

    // Handle Stripe checkout for registration payment
    const handleRegistrationPayment = async () => {
        if (!registrationData?.invoices?.[0]) {
            toast({
                title: "Error",
                description: "No invoice found for this registration.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        const invoice = registrationData.invoices[0];
        const invoiceSubtotal = invoice.invoice_line_items.reduce((acc, item) => acc + item.quantity * item.unit_price, 0) - invoice.amount_paid;
        
        // Apply GST when org GST is on, unless location-based GST exemption applies
        const applyGst = getEffectiveGstEnabled();
        const gstRate = applyGst ? (orgGstSettings.gst_rate / 100) : 0;
        const gstAmount = invoiceSubtotal * gstRate;
        const totalWithGst = invoiceSubtotal + gstAmount;

        setIsProcessingPayment(true);

        try {
            toast({
                title: "Redirecting to payment",
                description: "Please wait while we redirect you to the secure payment page...",
                status: "info",
                duration: 3000,
                isClosable: true,
            });

            // Prepare data for the edge function - send total including GST (if any)
            const requestData = {
                registration_id: registrationID,
                participant_email: registrationData.participant_email,
                participant_name: registrationData.participant_name,
                invoice_id: invoice.id,
                invoice_balance: totalWithGst, // Total including GST
                registration_fee_subtotal: invoiceSubtotal, // Pre-GST amount for reference
                gst_amount: gstAmount, // GST amount for reference
                gst_rate: orgGstSettings.gst_rate, // GST rate for reference
                gst_enabled: applyGst, // Effective GST (may be off for overseas e-learning)
                invoice_line_items: invoice.invoice_line_items || [],
                course_name: certificationRun?.full_certification?.name || 'Course Registration',
                course_start_date: certificationRun?.start_date 
                    ? moment(certificationRun.start_date).format('DD MMM YYYY') 
                    : null
            };

            console.log('Calling create-registration-checkout with data:', requestData);

            // Call the Supabase Edge Function
            const { data, error } = await supabase.functions.invoke("create-registration-checkout", {
                body: requestData
            });

            console.log('Edge Function response:', { data, error });

            if (error) {
                console.error('Edge Function error:', error);
                throw new Error(`Failed to create checkout session: ${error.message}`);
            }

            if (!data?.url) {
                console.error('Invalid response format:', data);
                throw new Error("Checkout URL not found in the response.");
            }

            // Redirect to Stripe checkout
            window.location.href = data.url;

        } catch (error) {
            console.error('Error processing payment:', error);
            toast({
                title: "Payment Error",
                description: error.message || "Failed to process payment. Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
            setIsProcessingPayment(false);
        }
    };

    if (loading) {
        return (
            <Center minH="100vh" bg="gray.50">
                <VStack spacing={4}>
                    <Spinner size="xl" color="brand.500" thickness="4px" />
                    <Text color="gray.600">Loading registration details...</Text>
                </VStack>
            </Center>
        );
    }

    if (!registrationData) {
        return (
            <Center minH="100vh" bg="gray.50">
                <VStack spacing={4}>
                    <Text color="gray.600" fontSize="lg">Registration not found.</Text>
                    <Button
                        leftIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/')}
                        colorScheme="brand"
                    >
                        Go Home
                    </Button>
                </VStack>
            </Center>
        );
    }

    const renderFieldValue = (fieldValue, fieldKey = '') => {
        // Handle null/undefined
        if (fieldValue === null || fieldValue === undefined || fieldValue === '') {
            return 'Not provided';
        }

        // Handle base64 images (signatures)
        if (typeof fieldValue === 'string' && fieldValue.startsWith('data:image/')) {
            return (
                <Box mt={1} p={2} bg="white" borderRadius="md" border="1px solid" borderColor="gray.100" display="inline-block">
                    <img 
                        src={fieldValue} 
                        alt="Signature" 
                        style={{ maxHeight: '80px', width: 'auto', display: 'block' }} 
                    />
                </Box>
            );
        }

        // Handle file paths (Supabase storage paths)
        const isFilePath = (val) => typeof val === 'string' && (val.startsWith('registrations/') || val.startsWith('full__cert/'));
        
        const renderFile = (path) => {
            const fileName = path.split('/').pop();
            const { data: urlData } = supabase.storage.from('Registration_data').getPublicUrl(path);
            const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);

            if (isImage) {
                return (
                    <Box mt={2} p={2} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.100" display="inline-block">
                        <Box
                            as="img"
                            src={urlData.publicUrl}
                            alt="Preview"
                            maxH="120px"
                            borderRadius="sm"
                            border="1px solid"
                            borderColor="gray.200"
                            fallbackSrc="https://via.placeholder.com/120?text=Image"
                            cursor="pointer"
                            onClick={() => window.open(urlData.publicUrl, '_blank')}
                        />
                    </Box>
                );
            }

            return (
                <Box mt={2} p={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.100" display="inline-block">
                    <HStack spacing={2} cursor="pointer" onClick={() => window.open(urlData.publicUrl, '_blank')}>
                        <Icon as={HiGlobeAlt} color="blue.500" />
                        <Text fontSize="xs" fontWeight="bold" color="blue.600">View Document</Text>
                    </HStack>
                </Box>
            );
        };

        if (isFilePath(fieldValue)) {
            return renderFile(fieldValue);
        }

        // Handle arrays
        if (Array.isArray(fieldValue)) {
            if (fieldValue.length === 0) return 'Not provided';
            
            // Check if array contains file paths
            if (isFilePath(fieldValue[0])) {
                return (
                    <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2} mt={1}>
                        {fieldValue.map((path, idx) => (
                            <Box key={idx}>{renderFile(path)}</Box>
                        ))}
                    </SimpleGrid>
                );
            }

            // Check if array contains objects (like education/work history)
            if (typeof fieldValue[0] === 'object') {
                return (
                    <VStack align="stretch" spacing={2} mt={1}>
                        {fieldValue.map((item, idx) => (
                            <Box key={idx} p={3} bg="gray.50" borderRadius="md" fontSize="xs" borderLeft="3px solid" borderColor="brand.200">
                                <SimpleGrid columns={2} spacing={2}>
                                    {Object.entries(item).map(([key, value]) => (
                                        <Box key={key}>
                                            <Text fontWeight="600" color="gray.500" textTransform="uppercase" fontSize="9px" letterSpacing="wider">
                                                {key.replace(/_/g, ' ')}
                                            </Text>
                                            <Text color="gray.800" fontWeight="500">{String(value || 'N/A')}</Text>
                                        </Box>
                                    ))}
                                </SimpleGrid>
                            </Box>
                        ))}
                    </VStack>
                );
            }
            
            // Array of primitives
            return fieldValue.join(', ');
        }

        // Handle objects (nested data)
        if (typeof fieldValue === 'object') {
            return (
                <Box p={3} bg="gray.50" borderRadius="md" mt={1}>
                    <SimpleGrid columns={2} spacing={3}>
                        {Object.entries(fieldValue).map(([key, value]) => (
                            <Box key={key}>
                                <Text fontWeight="600" color="gray.500" textTransform="uppercase" fontSize="9px" letterSpacing="wider">
                                    {key.replace(/_/g, ' ')}
                                </Text>
                                <Text color="gray.800" fontSize="xs" fontWeight="500">{String(value || 'N/A')}</Text>
                            </Box>
                        ))}
                    </SimpleGrid>
                </Box>
            );
        }

        // Handle booleans
        if (typeof fieldValue === 'boolean') {
            return fieldValue ? 'Yes' : 'No';
        }

        // Handle dates
        if (typeof fieldValue === 'string' && moment(fieldValue, moment.ISO_8601, true).isValid()) {
            return moment(fieldValue).format('DD MMM YYYY');
        }

        // Default: convert to string
        return String(fieldValue);
    };

    const renderAdditionalFields = (data) => {
        if (!data || typeof data !== 'object') return null;

        const sections = {};
        
        // Group fields by section
        Object.keys(data).forEach(key => {
            if (typeof data[key] === 'object' && data[key] !== null && !Array.isArray(data[key])) {
                sections[key] = data[key];
            } else {
                // Handle top-level fields not in a section
                if (!sections['General Information']) {
                    sections['General Information'] = {};
                }
                sections['General Information'][key] = data[key];
            }
        });

        if (Object.keys(sections).length === 0) return null;

        return (
            <Accordion allowMultiple defaultIndex={[0]} variant="separated">
                {Object.entries(sections).map(([sectionKey, sectionData], index) => {
                    const fields = Object.entries(sectionData)
                        .filter(([key]) => {
                            const lowKey = key.toLowerCase();
                            return !lowKey.includes('referral') && !lowKey.includes('promo');
                        })
                        .sort(([keyA], [keyB]) => {
                            // Move Photo to the top
                            if (keyA === 'documents_photograph_url') return -1;
                            if (keyB === 'documents_photograph_url') return 1;
                            
                            // Move Education Certificate right after Education
                            if (keyA === 'education' && keyB === 'documents_educational_certificate_url') return -1;
                            if (keyA === 'documents_educational_certificate_url' && keyB === 'education') return 1;
                            
                            return 0;
                        });

                    if (fields.length === 0) return null;

                    return (
                        <AccordionItem 
                            key={index} 
                            border="1px solid" 
                            borderColor="gray.200" 
                            borderRadius="lg" 
                            mb={4} 
                            overflow="hidden"
                            bg="white"
                            boxShadow="sm"
                        >
                            <AccordionButton
                                _expanded={{ bg: "brand.50", borderBottom: "1px solid", borderColor: "brand.100" }}
                                _hover={{ bg: "gray.50" }}
                                px={5}
                                py={4}
                            >
                                <Box flex="1" textAlign="left">
                                    <HStack spacing={3}>
                                        <Box bg="brand.100" p={1.5} borderRadius="md">
                                            <Icon as={HiInformationCircle} color="brand.600" boxSize={4} />
                                        </Box>
                                        <Text fontWeight="700" color="gray.800" fontSize="sm" textTransform="uppercase" letterSpacing="wide">
                                            {sectionKey.replace(/_/g, ' ')}
                                        </Text>
                                    </HStack>
                                </Box>
                                <AccordionIcon color="brand.500" />
                            </AccordionButton>
                            <AccordionPanel pb={6} pt={4} px={6}>
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={8} spacingY={6}>
                                    {fields.map(([fieldKey, fieldValue]) => {
                                        // Dynamic field labels
                                        let label = fieldKey.replace(/_/g, ' ');
                                        
                                        if (fieldKey === 'personal_details_gender') {
                                            label = 'Gender';
                                        } else if (fieldKey === 'documents_nric_verification_url') {
                                            const nationality = registrationData.nationality || '';
                                            if (nationality.includes('FIN')) {
                                                label = 'FIN Verification';
                                            } else if (nationality.includes('Passport')) {
                                                label = 'Passport Verification';
                                            } else {
                                                label = 'NRIC Verification';
                                            }
                                        } else if (fieldKey === 'documents_fin_passport_verification_url') {
                                            label = 'Passport Verification';
                                        } else if (fieldKey === 'legal_declaration_signature_url' || fieldKey === 'signature') {
                                            label = 'Signature';
                                        } else if (fieldKey === 'personal_details_work_status') {
                                            label = 'Work Status';
                                        } else if (fieldKey === 'documents_photograph_url') {
                                            label = 'Photo';
                                        } else if (fieldKey === 'documents_educational_certificate_url') {
                                            label = 'Education Certificate';
                                        }

                                        return (
                                            <Box key={fieldKey}>
                                                <Text fontSize="xs" color="gray.400" fontWeight="700" textTransform="uppercase" letterSpacing="wider" mb={1.5}>
                                                    {label}
                                                </Text>
                                                <Box>
                                                    {renderFieldValue(fieldValue, fieldKey)}
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </SimpleGrid>
                            </AccordionPanel>
                        </AccordionItem>
                    );
                })}
            </Accordion>
        );
    };

    return (
        <Box minH="100vh" bg="gray.50" py={8}>
            <Container maxW="container.lg">
                <MotionBox
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    {/* Success Header */}
                    <Box
                        bg="white"
                        borderRadius="xl"
                        p={8}
                        textAlign="center"
                        boxShadow="lg"
                        borderWidth="1px"
                        borderColor="green.200"
                        mb={6}
                    >
                        <VStack spacing={4}>
                            <Box
                                bg="green.100"
                                borderRadius="full"
                                p={4}
                            >
                                <CheckCircleIcon boxSize={12} color="green.500" />
                            </Box>
                            <Heading size="md" color="gray.800">
                                Registration Received!
                            </Heading>
                            <Text color="gray.600" fontSize="md">
                                Thank you for submitting your registration. Please review your information below.
                            </Text>
                            {/* <Badge colorScheme="green" fontSize="md" px={4} py={2} borderRadius="full">
                                Registration ID: {registrationID}
                            </Badge> */}
                        </VStack>
                    </Box>

                    {/* Alert for next steps */}
                    <Alert status="info" mb={6} borderRadius="lg">
                        <AlertIcon />
                        <Box>
                            <Text fontWeight="600" mb={1}>What's Next?</Text>
                            <Text fontSize="sm">
                                {registrationData.invoices[0] && registrationData.invoices[0].status === 'pending' && (() => {
                                    const inv = registrationData.invoices[0];
                                    const regFeeSubtotal = (inv.invoice_line_items || []).reduce((acc, item) => acc + (item.quantity || 0) * (item.unit_price || 0), 0);
                                    const balanceSubtotal = regFeeSubtotal - (inv.amount_paid || 0);
                                    const gstRate = getEffectiveGstEnabled() ? (orgGstSettings.gst_rate / 100) : 0;
                                    const totalDue = balanceSubtotal + (balanceSubtotal * gstRate);
                                    if (totalDue > 0) {
                                        return (
                                            <>
                                                Please make a registration payment of <strong>S$ {totalDue.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> to confirm your registration.
                                                {' '}
                                            </>
                                        );
                                    }
                                    return null;
                                })()}
                                We'll also review your registration and contact you via phone or email at{' '}
                                <strong>{registrationData.participant_email}</strong> or <strong>{registrationData.mobile_no}</strong> with further instructions.
                            </Text>
                        </Box>
                    </Alert>

                    {/* Certification Details */}
                    {certificationRun && (
                        <SectionBox title="Program Details" icon={HiGlobeAlt} mb={6}>
                            <VStack align="stretch" spacing={3}>
                                <Box>
                                    <Text fontSize="xs" color="gray.500" fontWeight="500">Program</Text>
                                    <Text fontSize="md" fontWeight="700" color="brand.700">
                                        {certificationRun.full_certification?.name}
                                    </Text>
                                </Box>
                                <Box>
                                    <Text fontSize="xs" color="gray.500" fontWeight="500">Run</Text>
                                    <Text fontSize="sm" fontWeight="600" color="gray.800">
                                        {certificationRun.run_name}
                                    </Text>
                                    <Text fontSize="xs" color="gray.600">
                                        {moment(certificationRun.start_date).format('DD MMM YYYY')} - {moment(certificationRun.end_date).format('DD MMM YYYY')}
                                    </Text>
                                </Box>
                                {certificationRun.course_run && certificationRun.course_run.length > 0 && (
                                    <Box>
                                        <Text fontSize="xs" color="gray.500" fontWeight="500" mb={2}>Included Modules</Text>
                                        <VStack align="stretch" spacing={1}>
                                            {certificationRun.course_run.map((course, idx) => (
                                                <HStack key={idx} spacing={2}>
                                                    <Badge colorScheme="blue" fontSize="xs">{course.course_type_code}</Badge>
                                                    <Text fontSize="xs" color="gray.700">{course.course_type.name}</Text>
                                                </HStack>
                                            ))}
                                        </VStack>
                                    </Box>
                                )}
                            </VStack>
                        </SectionBox>
                    )}

                    {/* Personal Information */}
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6}>
                        <SectionBox title="Personal Information" icon={HiUser}>
                            <VStack align="stretch" spacing={1} divider={<Divider />}>
                                <InfoRow icon={HiUser} label="Full Name" value={registrationData.participant_name} />
                                <InfoRow icon={HiMail} label="Email" value={registrationData.participant_email} />
                                <InfoRow icon={HiPhone} label="Mobile Number" value={registrationData.mobile_no} />
                                <InfoRow icon={HiIdentification} label="NRIC" value={registrationData.nric} />
                                <InfoRow 
                                    icon={HiCalendar} 
                                    label="Date of Birth" 
                                    value={registrationData.dob ? moment(registrationData.dob).format('DD MMM YYYY') : null} 
                                />
                                <InfoRow label="Age" value={registrationData.birth_age} />
                                <InfoRow label="Identification Type" value={registrationData.nationality} />
                            </VStack>
                        </SectionBox>

                        <SectionBox title="Registration Status" icon={CheckCircleIcon}>
                            <VStack align="stretch" spacing={3}>
                                <Box>
                                    <Text fontSize="xs" color="gray.500" fontWeight="500">Status</Text>
                                    <Badge colorScheme={registrationData.registration_stage === 'pending' ? 'yellow' : 'gray'} fontSize="sm">
                                        {registrationData.registration_stage === 'pending' ? 'Pending Review' : registrationData.registration_stage}
                                    </Badge>
                                </Box>
                                <Box>
                                    <Text fontSize="xs" color="gray.500" fontWeight="500">Submitted On</Text>
                                    <Text fontSize="sm" fontWeight="600" color="gray.800">
                                        {moment(registrationData.created_at).format('DD MMM YYYY, h:mm A')}
                                    </Text>
                                </Box>

                                {/* Invoice Details */}
                                {registrationData.invoices[0] && (() => {
                                    const inv = registrationData.invoices[0];
                                    const regFeeSubtotal = (inv.invoice_line_items || []).reduce((acc, item) => acc + (item.quantity || 0) * (item.unit_price || 0), 0);
                                    const isWaived = regFeeSubtotal === 0;
                                    return (
                                        <>
                                            <Box pt={3} borderTop="1px solid" borderColor="gray.200">
                                                <HStack justify="space-between" mb={2}>
                                                    <Text fontSize="xs" color="gray.500" fontWeight="600" textTransform="uppercase">
                                                        Invoice Details
                                                    </Text>
                                                    <Badge 
                                                        colorScheme={
                                                            isWaived ? 'green' :
                                                            inv.status === 'paid' ? 'green' : 
                                                            inv.status === 'pending' ? 'yellow' : 
                                                            'red'
                                                        } 
                                                        fontSize="xs"
                                                    >
                                                        {isWaived ? 'Waived' : inv.status}
                                                    </Badge>
                                                </HStack>
                                                {isWaived ? (
                                                    <Text fontSize="sm" color="gray.600" py={2}>
                                                        Registration fee has been waived.
                                                    </Text>
                                                ) : (
                                                    <>
                                                        {/* Line Items */}
                                                        {inv.invoice_line_items && inv.invoice_line_items.length > 0 && (
                                                            <>
                                                                <VStack align="stretch" spacing={1} mb={2}>
                                                                    {inv.invoice_line_items.map((item, idx) => (
                                                                        <HStack key={idx} justify="space-between" fontSize="xs">
                                                                            <Text color="gray.600">{item.description === 'Full Certification Registration Fee' ? 'Registration Fee' : item.description}</Text>
                                                                            <Text fontWeight="600" color="gray.800">
                                                                                S$ {(item.quantity * (item.unit_price || 0)).toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                            </Text>
                                                                        </HStack>
                                                                    ))}
                                                                </VStack>
                                                                {(() => {
                                                                    const gstRate = getEffectiveGstEnabled() ? (orgGstSettings.gst_rate / 100) : 0;
                                                                    const gstAmount = regFeeSubtotal * gstRate;
                                                                    const total = regFeeSubtotal + gstAmount;
                                                                    const amountPaid = inv.amount_paid || 0;
                                                                    const balanceDue = total - amountPaid;
                                                                    return (
                                                                        <VStack align="stretch" spacing={1} pt={2} borderTop="1px solid" borderColor="gray.100">
                                                                            <HStack justify="space-between" fontSize="xs">
                                                                                <Text color="gray.600">Subtotal:</Text>
                                                                                <Text fontWeight="600" color="gray.800">
                                                                                    S$ {regFeeSubtotal.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                                </Text>
                                                                            </HStack>
                                                                            {getEffectiveGstEnabled() && gstAmount > 0 && (
                                                                                <HStack justify="space-between" fontSize="xs">
                                                                                    <Text color="gray.600">GST ({orgGstSettings.gst_rate}%):</Text>
                                                                                    <Text fontWeight="600" color="gray.800">
                                                                                        S$ {gstAmount.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                                    </Text>
                                                                                </HStack>
                                                                            )}
                                                                            <HStack justify="space-between" fontSize="xs" pt={1}>
                                                                                <Text color="gray.700" fontWeight="600">Total:</Text>
                                                                                <Text fontWeight="700" color="gray.800">
                                                                                    S$ {total.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                                </Text>
                                                                            </HStack>
                                                                            {amountPaid > 0 && (
                                                                                <>
                                                                                    <HStack justify="space-between" fontSize="xs">
                                                                                        <Text color="gray.600">Amount Paid:</Text>
                                                                                        <Text fontWeight="600" color="green.600">
                                                                                            S$ {amountPaid.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                                        </Text>
                                                                                    </HStack>
                                                                                    <HStack justify="space-between" fontSize="xs">
                                                                                        <Text color="gray.600" fontWeight="600">Balance Due:</Text>
                                                                                        <Text fontWeight="700" color="brand.600">
                                                                                            S$ {balanceDue.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                                        </Text>
                                                                                    </HStack>
                                                                                </>
                                                                            )}
                                                                            <Box mt={2}>
                                                                                <Text fontSize="xs" color="gray.500">Due Date:</Text>
                                                                                <Text fontSize="xs" fontWeight="600" color="gray.700">
                                                                                    {moment(registrationData.created_at).format('DD MMM YYYY')}
                                                                                </Text>
                                                                            </Box>
                                                                        </VStack>
                                                                    );
                                                                })()}
                                                            </>
                                                        )}
                                                    </>
                                                )}
                                            </Box>
                                        </>
                                    );
                                })()}
                            </VStack>
                        </SectionBox>
                    </SimpleGrid>

                    {/* Additional Information */}
                    {registrationData.additional_data && Object.keys(registrationData.additional_data).length > 0 && (
                        <SectionBox title="Additional Information" icon={HiInformationCircle} mb={6}>
                            {renderAdditionalFields(registrationData.additional_data)}
                        </SectionBox>
                    )}

                    {/* Payment Options — show for approved/pending when not paid (fee 0/null → empty QR amount, same ref) */}
                    {(registrationData.registration_stage === 'approved' || registrationData.registration_stage === 'pending') &&
                     (!registrationData.invoices?.[0] || !['paid', 'payment received'].includes(
                         String(registrationData.invoices[0].status || '').toLowerCase()
                     )) && (
                        <SectionBox title="Payment Options" icon={HiCreditCard} mb={6} borderColor="brand.300" borderWidth="2px">
                            <VStack spacing={4} align="stretch">
                                {(() => {
                                    // Calculate registration fee with GST (null/missing line items → 0)
                                    const registrationFee = (registrationData.invoices?.[0]?.invoice_line_items || []).reduce(
                                        (acc, item) => acc + (item.quantity || 0) * (item.unit_price || 0),
                                        0
                                    );
                                    const gstRate = getEffectiveGstEnabled() ? (orgGstSettings.gst_rate / 100) : 0;
                                    const gstAmount = registrationFee * gstRate;
                                    const totalWithGst = registrationFee + gstAmount;
                                    
                                    return (
                                        <VStack spacing={2} py={3} px={4} bg="brand.50" borderRadius="md" border="1px solid" borderColor="brand.200">
                                            <Text fontSize="xs" fontWeight="bold" color="brand.600" textTransform="uppercase" letterSpacing="wider">Registration Fee Due</Text>
                                            <Box bg="white" p={3} borderRadius="md" w="100%" minW="200px">
                                                <VStack spacing={2} align="stretch">
                                                    <HStack justify="space-between">
                                                        <Text fontSize="sm" color="gray.600">Registration Fee:</Text>
                                                        <Text fontSize="sm" fontWeight="medium">S$ {registrationFee.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
                                                    </HStack>
                                                    {getEffectiveGstEnabled() && gstAmount > 0 && (
                                                        <HStack justify="space-between">
                                                            <Text fontSize="sm" color="gray.600">GST ({orgGstSettings.gst_rate}%):</Text>
                                                            <Text fontSize="sm" fontWeight="medium">S$ {gstAmount.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
                                                        </HStack>
                                                    )}
                                                    <Divider />
                                                    <HStack justify="space-between" align="center">
                                                        <Text fontSize="md" fontWeight="bold" color="gray.700">Total:</Text>
                                                        <Text fontSize="xl" fontWeight="bold" color="brand.700">S$ {totalWithGst.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
                                                    </HStack>
                                                </VStack>
                                            </Box>
                                        </VStack>
                                    );
                                })()}
                                
                                <VStack spacing={6} w="100%">
                                    {/* Option 1: PayNow Transfer - Most Prominent */}
                                    <Box 
                                        w="100%"
                                        p={6}
                                        borderWidth="2px"
                                        borderRadius="xl"
                                        bg="white"
                                        borderColor="green.300"
                                        boxShadow="md"
                                        _hover={{ borderColor: "green.400", boxShadow: "lg" }}
                                        transition="all 0.2s"
                                    >
                                        <VStack spacing={4} align="stretch">
                                            <HStack spacing={3} align="center">
                                                <Icon as={HiQrcode} boxSize={6} color="green.600" />
                                                <Text fontWeight="bold" color="green.700" fontSize="lg">PayNow Transfer</Text>
                                                <Badge colorScheme="green" ml="auto">Fastest</Badge>
                                            </HStack>
                                            
                                            <Box bg="green.50" p={4} borderRadius="lg" borderWidth="1px" borderColor="green.200">
                                                <VStack spacing={3} align="center">
                                                    {(() => {
                                                        const registrationFee = (registrationData.invoices?.[0]?.invoice_line_items || []).reduce(
                                                            (acc, item) => acc + (item.quantity || 0) * (item.unit_price || 0),
                                                            0
                                                        );
                                                        const gstRate = getEffectiveGstEnabled() ? (orgGstSettings.gst_rate / 100) : 0;
                                                        const totalWithGst = registrationFee + registrationFee * gstRate;
                                                        // Fee 0 → omit amount (manual entry); keep same reference notes
                                                        return (
                                                            <PayNowQRCode
                                                                uen={payNowOrg.uen}
                                                                amount={totalWithGst > 0 ? totalWithGst : null}
                                                                referenceNumber={payNowReference}
                                                                size={180}
                                                                showDownload
                                                                downloadLabel="Download QR Code"
                                                                downloadFileName={`PayNow-QR-${payNowReference || registrationID?.substring(0, 8)}.png`}
                                                            />
                                                        );
                                                    })()}
                                                </VStack>
                                            </Box>

                                            <VStack spacing={2} align="stretch" fontSize="sm">
                                                <Box bg="gray.50" p={3} borderRadius="md">
                                                    <Text fontWeight="semibold" color="gray.700" mb={1}>UEN No:</Text>
                                                    <Text color="gray.800" fontFamily="mono" fontSize="md">{payNowOrg.uen || '—'}</Text>
                                                </Box>
                                                <Box bg="gray.50" p={3} borderRadius="md">
                                                    <Text fontWeight="semibold" color="gray.700" mb={1}>Beneficiary Name:</Text>
                                                    <Text color="gray.800">{payNowOrg.name || '—'}</Text>
                                                </Box>
                                                <Box bg="green.100" p={3} borderRadius="md" borderWidth="1px" borderColor="green.300">
                                                    <Text fontWeight="bold" color="green.800" mb={1}>Reference Number:</Text>
                                                    <Text color="green.900" fontFamily="mono" fontSize="lg" fontWeight="bold">
                                                        {payNowReference}
                                                    </Text>
                                                </Box>
                                            </VStack>

                                            <Button 
                                                size="lg" 
                                                colorScheme="green" 
                                                w="100%"
                                                onClick={handlePayNowDone}
                                                isLoading={isUpdatingStatus}
                                                leftIcon={<Icon as={HiQrcode} />}
                                            >
                                                I have paid via PayNow
                                            </Button>
                                        </VStack>
                                    </Box>

                                    {/* Option 2: Bank Transfer — only when org bank details are set in TMS */}
                                    {isBankTransferConfigured() && (
                                    <Box 
                                        w="100%"
                                        p={6}
                                        borderWidth="2px"
                                        borderRadius="xl"
                                        bg="white"
                                        borderColor="purple.300"
                                        boxShadow="md"
                                        _hover={{ borderColor: "purple.400", boxShadow: "lg" }}
                                        transition="all 0.2s"
                                    >
                                        <VStack spacing={4} align="stretch">
                                            <HStack spacing={3} align="center">
                                                <Icon as={HiOfficeBuilding} boxSize={6} color="purple.600" />
                                                <Text fontWeight="bold" color="purple.700" fontSize="lg">Bank Transfer</Text>
                                            </HStack>

                                            <Accordion allowToggle defaultIndex={0}>
                                                <AccordionItem border="none">
                                                    <AccordionButton px={0} py={2} _hover={{ bg: "transparent" }}>
                                                        <Box flex="1" textAlign="left">
                                                            <Text fontWeight="semibold" color="gray.700">
                                                                View Bank Account Details
                                                            </Text>
                                                        </Box>
                                                        <AccordionIcon />
                                                    </AccordionButton>
                                                    <AccordionPanel px={0} pb={4}>
                                                        <VStack spacing={3} align="stretch" fontSize="sm">
                                                            <SimpleGrid columns={2} spacing={3}>
                                                                <Box bg="gray.50" p={3} borderRadius="md">
                                                                    <Text fontWeight="semibold" color="gray.700" mb={1} fontSize="xs">Bank Name</Text>
                                                                    <Text color="gray.800" fontWeight="medium">{getTenantBranding().bankName}</Text>
                                                                </Box>
                                                                {getTenantBranding().bankBranchCode && (
                                                                <Box bg="gray.50" p={3} borderRadius="md">
                                                                    <Text fontWeight="semibold" color="gray.700" mb={1} fontSize="xs">Bank/Branch Code</Text>
                                                                    <Text color="gray.800" fontFamily="mono" fontWeight="medium">{getTenantBranding().bankBranchCode}</Text>
                                                                </Box>
                                                                )}
                                                                {getTenantBranding().bankSwiftCode && (
                                                                <Box bg="gray.50" p={3} borderRadius="md">
                                                                    <Text fontWeight="semibold" color="gray.700" mb={1} fontSize="xs">SWIFT Code</Text>
                                                                    <Text color="gray.800" fontFamily="mono" fontWeight="medium">{getTenantBranding().bankSwiftCode}</Text>
                                                                </Box>
                                                                )}
                                                                {getTenantBranding().bankAccountType && (
                                                                <Box bg="gray.50" p={3} borderRadius="md">
                                                                    <Text fontWeight="semibold" color="gray.700" mb={1} fontSize="xs">Account Type</Text>
                                                                    <Text color="gray.800" fontWeight="medium">{getTenantBranding().bankAccountType}</Text>
                                                                </Box>
                                                                )}
                                                            </SimpleGrid>
                                                            <Box bg="purple.50" p={3} borderRadius="md" borderWidth="1px" borderColor="purple.200">
                                                                <Text fontWeight="semibold" color="gray.700" mb={1} fontSize="xs">Account Number</Text>
                                                                <Text color="purple.800" fontFamily="mono" fontSize="lg" fontWeight="bold">
                                                                    {getTenantBranding().bankAccountNumber}
                                                                </Text>
                                                            </Box>
                                                            <Box bg="gray.50" p={3} borderRadius="md">
                                                                <Text fontWeight="semibold" color="gray.700" mb={1} fontSize="xs">Beneficiary Name</Text>
                                                                <Text color="gray.800">{getTenantBranding().bankBeneficiaryName || getTenantBranding().name}</Text>
                                                            </Box>
                                                            <Box bg="purple.100" p={3} borderRadius="md" borderWidth="1px" borderColor="purple.300">
                                                                <Text fontWeight="bold" color="purple.800" mb={1} fontSize="xs">Reference Number</Text>
                                                                <Text color="purple.900" fontFamily="mono" fontSize="lg" fontWeight="bold">
                                                                    {payNowReference}
                                                                </Text>
                                                            </Box>
                                                        </VStack>
                                                    </AccordionPanel>
                                                </AccordionItem>
                                            </Accordion>

                                            <Button 
                                                size="lg" 
                                                colorScheme="purple" 
                                                w="100%"
                                                onClick={handlePayNowDone}
                                                isLoading={isUpdatingStatus}
                                                leftIcon={<Icon as={HiOfficeBuilding} />}
                                            >
                                                I have paid via Bank Transfer
                                            </Button>
                                        </VStack>
                                    </Box>
                                    )}

                                    {/* Option 3: Stripe — only when tenant has Stripe keys configured */}
                                    {stripeEnabled && (
                                    <Box 
                                        w="100%"
                                        p={6}
                                        borderWidth="2px"
                                        borderRadius="xl"
                                        bg="white"
                                        borderColor="blue.300"
                                        boxShadow="md"
                                        _hover={{ borderColor: "blue.400", boxShadow: "lg" }}
                                        transition="all 0.2s"
                                    >
                                        <VStack spacing={4} align="stretch">
                                            <HStack spacing={3} align="center">
                                                <Icon as={HiCreditCard} boxSize={6} color="blue.600" />
                                                <Text fontWeight="bold" color="blue.700" fontSize="lg">Stripe</Text>
                                                <Badge colorScheme="blue" ml="auto">Secure</Badge>
                                            </HStack>
                                            
                                            <Box bg="blue.50" p={4} borderRadius="lg" borderWidth="1px" borderColor="blue.200">
                                                <VStack spacing={2} align="center">
                                                    <Icon as={HiCreditCard} boxSize={10} color="blue.500" />
                                                    <Text fontSize="sm" color="gray.700" textAlign="center">
                                                        Pay securely with your credit or debit card
                                                    </Text>
                                                </VStack>
                                            </Box>

                                            <Button 
                                                size="lg" 
                                                colorScheme="blue" 
                                                w="100%"
                                                onClick={handleRegistrationPayment}
                                                isLoading={isProcessingPayment}
                                                leftIcon={<Icon as={HiCreditCard} />}
                                            >
                                                Pay via Stripe
                                            </Button>
                                        </VStack>
                                    </Box>
                                    )}
                                </VStack>
                            </VStack>
                        </SectionBox>
                    )}

                    {/* Action Buttons */}
                    <Stack direction={{ base: 'column', md: 'row' }} spacing={4} justify="center">
                        {/* Only show Edit Registration button when registration is in draft or rejected stage */}
                        {(registrationData.registration_stage === 'draft' || registrationData.registration_stage === 'rejected') && (
                            <Button
                                leftIcon={<EditIcon />}
                                onClick={() => navigate(fullCertRegistrationPath(registrationID))}
                                colorScheme="orange"
                                size="lg"
                                variant="outline"
                            >
                                Edit Registration
                            </Button>
                        )}
                    </Stack>

                    {/* Footer Note */}
                    <Box mt={8} p={4} bg="blue.50" borderRadius="lg" textAlign="center">
                        <Text fontSize="sm" color="blue.800">
                            If you have any questions, please contact us at{' '}
                            {orgContactEmail ? (
                                <Text as="a" href={`mailto:${orgContactEmail}`} fontWeight="bold" _hover={{ textDecoration: 'underline' }}>
                                    {orgContactEmail}
                                </Text>
                            ) : (
                                <Text as="span" fontWeight="bold">our support team</Text>
                            )}
                        </Text>
                    </Box>
                </MotionBox>
            </Container>
        </Box>
    );
};

export default RegistrationPending;

